import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  useGetCompanyPositionsQuery,
  useSelectMyPositionMutation,
  useCreateCompanyPositionMutation,
  useUploadAvatarMutation,
} from "../../services/api";
import { selectIsOwner, selectInviteCode } from "../../store/slices/authSlice";
import "./Onboarding.css";

const Onboarding = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const isOwner = useSelector(selectIsOwner);
  const inviteCode = useSelector(selectInviteCode);

  // State
  const [selectedPosition, setSelectedPosition] = useState("");
  const [profileImage, setProfileImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);

  // Owner-specific state for creating positions
  const [newPositionName, setNewPositionName] = useState("");
  const [createdPositions, setCreatedPositions] = useState([]);

  // Get company ID from user
  const companyId = user?.company?._id || user?.company;
  const companyName = user?.company?.name || "your company";

  // Format invite code as XXXX-XXXX
  const formattedInviteCode = inviteCode
    ? `${inviteCode.slice(0, 4)}-${inviteCode.slice(4)}`
    : "";

  // Redirect if no company
  useEffect(() => {
    if (!companyId) {
      navigate("/company-selection");
    }
  }, [companyId, navigate]);

  // Fetch company positions (for employees to select)
  const { data: positions, isLoading: positionsLoading } = useGetCompanyPositionsQuery(
    companyId,
    { skip: !companyId }
  );

  // Mutations
  const [selectPosition] = useSelectMyPositionMutation();
  const [createPosition] = useCreateCompanyPositionMutation();
  const [uploadAvatar] = useUploadAvatarMutation();

  // Handle image selection (preview; uploaded on complete)
  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Remove selected image
  const handleRemoveImage = () => {
    setProfileImage(null);
    setImagePreview(null);
  };

  // Copy invite code
  const handleCopyCode = () => {
    navigator.clipboard.writeText(formattedInviteCode);
    setCodeCopied(true);
    setTimeout(() => setCodeCopied(false), 2000);
  };

  // Owner: Add a new position
  const handleAddPosition = async () => {
    const trimmedName = newPositionName.trim();
    if (!trimmedName) return;

    try {
      await createPosition({
        companyId,
        name: trimmedName,
        description: "",
      }).unwrap();
      setCreatedPositions([...createdPositions, trimmedName]);
      setNewPositionName("");
    } catch (error) {
      console.error("Error creating position:", error);
    }
  };

  const handlePositionKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddPosition();
    }
  };

  // Handle complete setup
  const handleComplete = async () => {
    setIsSubmitting(true);
    try {
      // Employee: save position selection
      if (!isOwner && selectedPosition) {
        await selectPosition({ positionId: selectedPosition }).unwrap();
      }
      // Upload profile image if selected
      if (profileImage) {
        const formData = new FormData();
        formData.append("avatar", profileImage);
        await uploadAvatar(formData).unwrap();
      }
      navigate("/dashboard");
    } catch (error) {
      console.error("Error completing onboarding:", error);
      navigate("/dashboard");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle skip
  const handleSkip = () => {
    navigate("/dashboard");
  };

  return (
    <div className="onboarding-page">
      <div className="onboarding-container">
        <div className="onboarding-card">
          <div className="onboarding-header">
            <div className="welcome-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            </div>
            <h1>
              {isOwner ? `${companyName} is ready!` : `Welcome to ${companyName}!`}
            </h1>
            <p>
              {isOwner
                ? "Let's finish setting up your company."
                : "Let's get you set up. This will only take a moment."}
            </p>
          </div>

          <div className="onboarding-steps">
            {/* Step 1: Profile Photo (Mock) - Same for both */}
            <div className="onboarding-step">
              <div className="step-header">
                <span className="step-number">1</span>
                <h2>Add Profile Photo</h2>
                <span className="optional-badge">Optional</span>
              </div>
              <div className="photo-upload-section">
                <div className="photo-preview">
                  {imagePreview ? (
                    <>
                      <img src={imagePreview} alt="Profile preview" />
                      <button
                        type="button"
                        className="remove-photo-btn"
                        onClick={handleRemoveImage}
                      >
                        &times;
                      </button>
                    </>
                  ) : (
                    <div className="photo-placeholder">
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                        <circle cx="12" cy="7" r="4" />
                      </svg>
                    </div>
                  )}
                </div>
                <div className="photo-upload-controls">
                  <label className="upload-btn">
                    {imagePreview ? "Change Photo" : "Upload Photo"}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageSelect}
                      hidden
                    />
                  </label>
                  <p className="upload-hint">JPG, PNG or GIF. Max 5MB.</p>
                </div>
              </div>
            </div>

            {/* OWNER FLOW: Create Positions + Invite Code */}
            {isOwner && (
              <>
                {/* Step 2: Create Positions */}
                <div className="onboarding-step">
                  <div className="step-header">
                    <span className="step-number">2</span>
                    <h2>Add Team Roles</h2>
                    <span className="optional-badge">Optional</span>
                  </div>
                  <p className="step-description">
                    Define roles for your team (e.g., Chef, Manager, Waiter).
                    Team members can select their role when they join.
                  </p>
                  <div className="add-position-row">
                    <input
                      type="text"
                      value={newPositionName}
                      onChange={(e) => setNewPositionName(e.target.value)}
                      onKeyDown={handlePositionKeyDown}
                      placeholder="Enter role name"
                      maxLength={50}
                    />
                    <button
                      type="button"
                      className="btn-add-role"
                      onClick={handleAddPosition}
                      disabled={!newPositionName.trim()}
                    >
                      Add
                    </button>
                  </div>
                  {(positions?.length > 0 || createdPositions.length > 0) && (
                    <div className="positions-list">
                      {positions?.map((pos) => (
                        <span key={pos._id} className="position-tag">
                          {pos.name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Step 3: Invite Code */}
                <div className="onboarding-step">
                  <div className="step-header">
                    <span className="step-number">3</span>
                    <h2>Invite Your Team</h2>
                  </div>
                  <p className="step-description">
                    Share this code with your team members so they can join your company.
                  </p>
                  <div className="invite-code-display">
                    <span className="invite-code">{formattedInviteCode}</span>
                    <button
                      type="button"
                      className="copy-code-btn"
                      onClick={handleCopyCode}
                    >
                      {codeCopied ? "Copied!" : "Copy"}
                    </button>
                  </div>
                  <p className="invite-hint">
                    You can also find this code later in the Team page.
                  </p>
                </div>
              </>
            )}

            {/* EMPLOYEE FLOW: Select Position */}
            {!isOwner && (
              <>
                {positionsLoading ? (
                  <div className="onboarding-step">
                    <div className="step-header">
                      <span className="step-number">2</span>
                      <h2>Select Your Role</h2>
                    </div>
                    <div className="loading-positions">Loading roles...</div>
                  </div>
                ) : positions && positions.length > 0 ? (
                  <div className="onboarding-step">
                    <div className="step-header">
                      <span className="step-number">2</span>
                      <h2>Select Your Role</h2>
                      <span className="optional-badge">Optional</span>
                    </div>
                    <p className="step-description">
                      Choose the role that best describes your position in the company.
                    </p>
                    <div className="position-grid">
                      {positions.map((pos) => (
                        <button
                          key={pos._id}
                          type="button"
                          className={`position-option ${selectedPosition === pos._id ? "selected" : ""}`}
                          onClick={() => setSelectedPosition(pos._id)}
                        >
                          <span className="position-name">{pos.name}</span>
                          {pos.description && (
                            <span className="position-desc">{pos.description}</span>
                          )}
                          {selectedPosition === pos._id && (
                            <span className="check-icon">&#10003;</span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="onboarding-step">
                    <div className="step-header">
                      <span className="step-number">2</span>
                      <h2>You're All Set!</h2>
                    </div>
                    <p className="step-description">
                      Your company hasn't set up team roles yet. You can update your role later in Settings.
                    </p>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="onboarding-actions">
            <button
              className="complete-btn"
              onClick={handleComplete}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Setting up..." : isOwner ? "Go to Dashboard" : "Complete Setup"}
            </button>
            <button
              className="skip-btn"
              onClick={handleSkip}
              disabled={isSubmitting}
            >
              Skip for now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
